import { useNavigate, Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { authService } from '../services'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Globe, Check } from 'lucide-react'
import { useLanguage } from '../hooks/useLanguage'
import { languages } from '../i18n'
import OptimizedImageByName from '../components/common/OptimizedImageByName'
import { imageAssets } from '../assets/images'
  
// Import logo
import logo from '../assets/logo.svg'

export default function Welcome() {
  const { t } = useTranslation()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const { currentLanguage, changeLanguage } = useLanguage()
  const navigate = useNavigate()

  useEffect(() => {
    // Check authentication status
    setIsAuthenticated(authService.isAuthenticated())
    
    // Preload critical images for faster loading
    const preloadImages = () => {
      // Preload AVIF versions for best performance, browsers will fallback if not supported
      const imagesToPreload = [
        imageAssets.servicesFlower.avif,
        imageAssets.servicesFruit.avif,
        imageAssets.servicesCatGrass.avif
      ]
      
      imagesToPreload.forEach(src => {
        const link = document.createElement('link')
        link.rel = 'preload'
        link.as = 'image'
        link.href = src
        document.head.appendChild(link)
      })
    }
    
    preloadImages()
  }, [])

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.language-switcher')) {
        setShowLanguageDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleServiceSelect = (serviceType: 'flowers' | 'fruits' | 'cat-grass') => {
    // Direct navigation to service page regardless of authentication status
    navigate(`/${serviceType}`)
  }

  const handleLanguageChange = (lng: string) => {
    changeLanguage(lng)
    setShowLanguageDropdown(false)
  }

  // Get current language display name
  const getCurrentLanguageDisplay = () => {
    return languages[currentLanguage as keyof typeof languages]?.nativeName || currentLanguage.toUpperCase();
  };

  return (
    <div className="min-h-screen flex flex-col bg-white relative">
      {/* Language Switcher */}
      <div className="absolute top-0 right-4 z-50 language-switcher">
        <div className="relative">
          <button
            onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
            className="flex items-center gap-2 bg-white rounded-full p-3 border-none"
          >
            <Globe className="h-5 w-5 text-black" />
            {/* <span className="text-sm text-black">{getCurrentLanguageDisplay()}</span> */}
          </button>
          
          {showLanguageDropdown && (
            <div className="absolute top-full right-0 mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="py-2">
                {Object.keys(languages).map((lng) => {
                  const isSelected = currentLanguage === lng;
                  return (
                    <button
                      key={lng}
                      onClick={() => handleLanguageChange(lng)}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors flex items-center justify-between border-none ${
                        isSelected ? 'bg-white text-black' : 'bg-white text-gray-700'
                      }`}
                    >
                      <span>{languages[lng as keyof typeof languages].nativeName}</span>
                      {isSelected && (
                        <div className="w-2 h-2 bg-black rounded-full"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 px-6 flex flex-col overflow-auto pb-24">
        {/* Header */}
        <div className="text-center mt-8 mb-4">
          <div className="flex justify-center mb-4">
            <img 
              src={logo} 
              alt="Piika - Premium Flower and Fruit Subscription Service" 
              className="h-12 w-auto" 
              width="auto"
              height="48"
              loading="eager"
              decoding="async"
            />
          </div>
          <h2 className="text-xl font-bold mb-2 text-center text-[#262626]">
            {t('welcome.services.title')}
          </h2>
          <p className="text-gray-600 text-base mb-4">
            {t('welcome.services.subtitle')}
          </p>
          
          {/* Location Badge */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-full px-4 py-2 mb-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-green-700 font-medium text-sm">
              {t('welcome.services.location.text')} {t('welcome.services.location.city')}
            </span>
            <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {t('welcome.services.location.badge')}
            </span>
          </div>
        </div>

        {/* Services Grid */}
        <div className="flex-1 flex flex-col gap-5 max-w-md mx-auto w-full">
          {/* Flower Subscription Card */}
          <div 
            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 cursor-pointer transform transition-all duration-200 hover:shadow-xl hover:-translate-y-1"
            onClick={() => handleServiceSelect('flowers')}
          >
            <div className="flex items-center mb-4">
              <div className="w-16 h-16 rounded-2xl bg-yellow-100 flex items-center justify-center mr-4 flex-shrink-0">
                <OptimizedImageByName 
                  imageName="servicesFlower" 
                  alt="Fresh flower bouquet subscription service"
                  className="w-12 h-12 object-contain" 
                  loading="eager"
                  width={48}
                  height={48}
                />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-xl text-gray-800 mb-1">
                  {t('welcome.services.flowers.title')}
                </h3>
                <p className="text-gray-600 text-sm">
                  {t('welcome.services.flowers.description')}
                </p>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-400" />
            </div>
            
            {/* Features */}
            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span>{t('welcome.services.flowers.feature1')}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span>{t('welcome.services.flowers.feature2')}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span>{t('welcome.services.flowers.feature3')}</span>
              </div>
            </div>
          </div>

          {/* Fruit Subscription Card */}
          <div 
            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 cursor-pointer transform transition-all duration-200 hover:shadow-xl hover:-translate-y-1"
            onClick={() => handleServiceSelect('fruits')}
          >
            
            <div className="flex items-center mb-4">
              <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mr-4 flex-shrink-0">
                <OptimizedImageByName 
                  imageName="servicesFruit" 
                  alt="Fresh fruit subscription delivery service"
                  className="w-12 h-12 object-contain" 
                  loading="eager"
                  width={48}
                  height={48}
                />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-xl text-gray-800 mb-1">
                  {t('welcome.services.fruits.title')}
                </h3>
                <p className="text-gray-600 text-sm">
                  {t('welcome.services.fruits.description')}
                </p>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-400" />
            </div>
            
            {/* Features */}
            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span>{t('welcome.services.fruits.feature1')}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span>{t('welcome.services.fruits.feature2')}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span>{t('welcome.services.fruits.feature3')}</span>
              </div>
            </div>
          </div>

          {/* Cat Grass Subscription Card */}
          <div 
            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 cursor-pointer transform transition-all duration-200 hover:shadow-xl hover:-translate-y-1 relative"
            onClick={() => handleServiceSelect('cat-grass')}
          >
            {/* New Service Badge */}
            <div className="absolute -top-2 -right-2 bg-orange-400 text-white text-xs font-bold px-3 py-1 rounded-full">
              {t('welcome.services.catGrass.badge')}
            </div>
            <div className="flex items-center mb-4">
              <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mr-4 flex-shrink-0">
                <OptimizedImageByName 
                  imageName="servicesCatGrass" 
                  alt="Fresh cat grass subscription for indoor cats"
                  className="w-12 h-12 object-contain" 
                  loading="eager"
                  width={48}
                  height={48}
                />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-xl text-gray-800 mb-1">
                  {t('welcome.services.catGrass.title')}
                </h3>
                <p className="text-gray-600 text-sm">
                  {t('welcome.services.catGrass.description')}
                </p>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-400" />
            </div>
            
            {/* Features */}
            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span>{t('welcome.services.catGrass.feature1')}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span>{t('welcome.services.catGrass.feature2')}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span>{t('welcome.services.catGrass.feature3')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Action */}
      <div className="p-4 fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t">
        {!isAuthenticated ? (
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              {t('welcome.login.existingAccount')}{' '}
              <Link to="/login" className="text-blue-600 font-medium">
                {t('welcome.login.loginLink')}
              </Link>
            </p>
          </div>
        ) : (
          <div className="text-center">
            <Link 
              to="/subscriptions" 
              className="inline-flex items-center justify-center w-full bg-yellow-400 text-gray-800 font-semibold py-3 px-6 rounded-xl hover:bg-yellow-500 transition-colors duration-200"
            >
              {t('welcome.subscription.viewSubscriptions')}
            </Link>
            <p className="text-gray-600 text-sm mt-2">
              {t('welcome.subscription.manageAccount')}
            </p>
          </div>
        )}
      </div>
      

    </div>
  )
}